import React, { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useCreateNotification } from '@/hooks/useNotifications'
import { useUsers } from '@/hooks/useUsers'

const NOTIFICATION_TYPES = [
  { value: 'system_announcement', label: 'System Announcement' },
]

const RECIPIENT_ROLES = [
  { value: 'student', label: 'Student' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'affiliate', label: 'Affiliate' },
]

export const AdminNotificationsPage: React.FC = () => {
  const createMutation = useCreateNotification()
  const [recipientMode, setRecipientMode] = useState<'user' | 'role'>('role')

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'system_announcement',
    recipient_user_id: '',
    recipient_role: 'student',
    action_link: '',
  })

  const [userSearch, setUserSearch] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const { data: users } = useUsers({
    search: userSearch || undefined,
    limit: 50,
  })

  const userList = users || []

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSuccessMessage('')
    setErrorMessage('')

    const payload: Record<string, unknown> = {
      type: formData.type,
      title: formData.title,
      message: formData.message,
    }

    if (recipientMode === 'user' && formData.recipient_user_id) {
      payload.recipient_user_id = formData.recipient_user_id
    } else if (recipientMode === 'role' && formData.recipient_role) {
      payload.recipient_role = formData.recipient_role
    }

    if (formData.action_link) {
      payload.action_link = formData.action_link
    }

    try {
      await createMutation.mutateAsync(payload as Parameters<typeof createMutation.mutateAsync>[0], {
        onSuccess: () => {
          setSuccessMessage('Notifikasi berhasil dikirim!')
          setFormData({
            title: '',
            message: '',
            type: 'system_announcement',
            recipient_user_id: '',
            recipient_role: 'student',
            action_link: '',
          })
          setTimeout(() => setSuccessMessage(''), 3000)
        },
        onError: (error: unknown) => {
          const msg = error instanceof Error ? error.message : 'Gagal mengirim notifikasi'
          setErrorMessage(msg)
        },
      })
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Gagal mengirim notifikasi'
      setErrorMessage(msg)
    }
  }

  return (
    <div>
      <PageHeader
        title="Kirim Notifikasi"
        subtitle="Kirim notifikasi ke pengguna atau role tertentu"
      />

      <div className="rounded-lg bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 overflow-hidden shadow-sm max-w-2xl">
        <div className="px-6 py-5 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02]">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <span className="material-symbols-outlined text-primary">notifications_active</span>
            Compose Notification
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Isi form di bawah untuk mengirim notifikasi
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {successMessage && (
            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-sm font-medium flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-medium flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">error</span>
              {errorMessage}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Judul <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Judul notifikasi"
              className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Pesan <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={4}
              placeholder="Isi pesan notifikasi"
              className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary resize-none"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Tipe <span className="text-red-500">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              {NOTIFICATION_TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Recipient Mode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Penerima <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="recipientMode"
                  value="role"
                  checked={recipientMode === 'role'}
                  onChange={() => setRecipientMode('role')}
                  className="w-4 h-4 text-primary border-slate-300 focus:ring-primary"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">By Role</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="recipientMode"
                  value="user"
                  checked={recipientMode === 'user'}
                  onChange={() => setRecipientMode('user')}
                  className="w-4 h-4 text-primary border-slate-300 focus:ring-primary"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Specific User</span>
              </label>
            </div>

            {recipientMode === 'role' ? (
              <select
                name="recipient_role"
                value={formData.recipient_role}
                onChange={handleChange}
                className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                {RECIPIENT_ROLES.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Cari nama atau email pengguna..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
                <select
                  name="recipient_user_id"
                  value={formData.recipient_user_id}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                >
                  <option value="">-- Pilih Pengguna --</option>
                  {userList.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Action Link (optional) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Action Link <span className="text-slate-400 text-xs">(opsional)</span>
            </label>
            <input
              type="text"
              name="action_link"
              value={formData.action_link}
              onChange={handleChange}
              placeholder="https://example.com/target"
              className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-surface-card px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Mengirim...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-[18px]">send</span>
                  Kirim Notifikasi
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AdminNotificationsPage
